// Pure helper for the Inventory duplicate-finder UI. Groups items by
// a normalized name key (case-insensitive, whitespace-collapsed, trimmed)
// and returns only the groups that have 2+ entries so the panel can
// render "groups needing cleanup" without further filtering.
//
// v1 uses exact-normalized matching (no fuzzy / Levenshtein). Rationale:
// false positives in a destructive workflow (operators are about to
// delete / merge things) are worse than missing a typo by one letter.
// A separate fuzzy lane can be layered on later if operators ask for it.

/** Collapse whitespace, trim ends, lowercase. Empty strings map to "". */
export function normalizeName(name: string): string {
  return name.replace(/\s+/g, " ").trim().toLowerCase();
}

export type DuplicateGroup<T> = {
  /** Normalized key (already through `normalizeName`). */
  key: string;
  /** Display label (the first item's original name, for the group heading). */
  label: string;
  items: T[];
};

/**
 * Group items whose normalized name matches and return only multi-entry
 * groups (length >= 2). Output is sorted by group size descending then
 * by label ascending so the most-duplicated names rise to the top.
 */
export function findDuplicateGroups<T>(
  items: ReadonlyArray<T>,
  getName: (item: T) => string
): DuplicateGroup<T>[] {
  const buckets = new Map<string, { label: string; items: T[] }>();
  for (const item of items) {
    const original = getName(item) ?? "";
    const key = normalizeName(original);
    if (!key) continue;
    const existing = buckets.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      buckets.set(key, { label: original.trim(), items: [item] });
    }
  }
  const groups: DuplicateGroup<T>[] = [];
  for (const [key, value] of buckets) {
    if (value.items.length >= 2) {
      groups.push({ key, label: value.label, items: value.items });
    }
  }
  groups.sort((a, b) => {
    if (b.items.length !== a.items.length) return b.items.length - a.items.length;
    return a.label.localeCompare(b.label);
  });
  return groups;
}
